// Stage ② 계약 카드 — ops-britzmedi margin/ContractBoard.tsx 를 Supabase contracts
// 1급 테이블 기반으로 개작 (FR-8). 조건·타임라인 편집 + 쿼터 달성 보드 + 페이스 진단.
// 저장: 로컬 상태 + 800ms 디바운스 → contractsRepo.update (blob 자동저장 UX 승계).
import { useEffect, useMemo, useRef, useState } from "react";
import { contractsRepo, type NewContract } from "@/lib/db";
import type { ContractRow, ContractTier, Partner, Product } from "@/types";
import type { Contract, SaleRow } from "@/lib/margin/model";
import { contractPace, contractProgress, emptyFree, krw, pct, usd } from "@/lib/margin/model";
import { contractRowToModel } from "./convert";
import { btn, NumInput, TextInput } from "./ui";
import { ContractTimeline, PaceChart } from "./ContractTimeline";

/** 구간표 + 누적 판매를 겹친 수평 달성 트랙 (원본 QuotaTrack 이식). */
export function QuotaTrack({ ct, sold }: { ct: Contract; sold: number }): JSX.Element {
  const sorted = [...ct.tiers].sort((a, b) => (a.fromQty || 0) - (b.fromQty || 0));
  if (sorted.length === 0) return <p className="text-xs text-slate-400">구간표가 비어 있습니다.</p>;
  const lastTo = sorted.reduce<number | null>((acc, t) => (t.toQty != null ? Math.max(acc ?? 0, t.toQty) : acc), null);
  // 트랙 끝 = 목표수량 > 마지막 상한 > 판매량 중 최댓값 (열린 구간 대비)
  const end = Math.max(ct.targetQty ?? 0, lastTo ?? 0, sold, 1);
  const pos = (q: number) => Math.min(100, Math.max(0, (q / end) * 100));
  const fillPct = pos(sold);
  return (
    <div className="mt-1">
      <div className="relative h-7 w-full overflow-hidden rounded border border-slate-300 bg-slate-100">
        {sorted.map((t, i) => {
          const from = pos((t.fromQty || 1) - 1);
          const to = t.toQty != null ? pos(t.toQty) : 100;
          return (
            <div
              key={i}
              className={"absolute inset-y-0 border-r border-slate-300 " + (i % 2 ? "bg-slate-200/60" : "bg-slate-100")}
              style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%` }}
            />
          );
        })}
        <div className="absolute inset-y-0 left-0 bg-emerald-500/80" style={{ width: `${fillPct}%` }} title={`누적 ${sold}대`} />
        {ct.targetQty != null && (
          <div className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: `${pos(ct.targetQty)}%` }} title={`목표 ${ct.targetQty}대`} />
        )}
        <div className="absolute -top-0 h-full w-0.5 bg-emerald-800" style={{ left: `${fillPct}%` }} />
        <span
          className="absolute top-1/2 -translate-y-1/2 rounded bg-emerald-700 px-1 text-[10px] font-bold text-white"
          style={{ left: `min(max(${fillPct}% - 14px, 2px), calc(100% - 44px))` }}
        >
          {usd(sold)}대
        </span>
      </div>
      <div className="relative mt-0.5 h-4 w-full text-[10px] text-slate-500">
        {sorted.map((t, i) => {
          const from = pos((t.fromQty || 1) - 1);
          const to = t.toQty != null ? pos(t.toQty) : 100;
          return (
            <span key={i} className="absolute truncate text-center tabular-nums" style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%` }}>
              {t.fromQty}
              {t.toQty != null ? `–${t.toQty}` : "+"} · ${usd(t.price)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const EDITABLE: (keyof NewContract)[] = [
  "name", "destination_country", "country_name", "buyer_id", "buyer_name", "product_id",
  "currency", "start_date", "end_date", "demo_start", "demo_end", "demo_qty",
  "license_label", "license_start", "license_end", "license_done", "quota_start",
  "tiers", "free", "target_qty", "status", "note",
];

export default function ContractCard({
  contract,
  sales,
  partners,
  products,
  today,
  onChanged,
  onDeleted,
}: {
  contract: ContractRow;
  /** 이 계약의 확정 실적 (모델 SaleRow 변환본). */
  sales: SaleRow[];
  partners: Partner[];
  products: Product[];
  today: string;
  onChanged?: (row: ContractRow) => void;
  onDeleted?: () => void;
}): JSX.Element {
  const [row, setRow] = useState<ContractRow>(contract);
  const [saveMsg, setSaveMsg] = useState<string>("");
  const timer = useRef<number | null>(null);
  const skipFirst = useRef(true);

  useEffect(() => setRow(contract), [contract.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 디바운스 자동 저장 — blob 마진 데스크의 자동저장 UX 승계.
  useEffect(() => {
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    setSaveMsg("저장 중…");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const patch: Partial<NewContract> = {};
      for (const k of EDITABLE) (patch as Record<string, unknown>)[k] = row[k as keyof ContractRow];
      contractsRepo
        .update(row.id, patch)
        .then((saved) => {
          setSaveMsg("저장됨");
          onChanged?.(saved);
        })
        .catch(() => setSaveMsg("저장 실패 (네트워크/설정 확인)"));
    }, 800);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const up = (v: Partial<ContractRow>) => setRow((p) => ({ ...p, ...v }));

  const ct: Contract = useMemo(() => contractRowToModel(row), [row]);
  const pr = contractProgress(ct, sales);

  const dateIn = (value: string | null, set: (v: string | null) => void, title?: string) => (
    <input
      type="date"
      value={value ?? ""}
      title={title}
      onChange={(e) => set(e.target.value || null)}
      className="rounded border border-slate-300 px-1 py-0.5 text-xs"
    />
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <TextInput value={row.name} onChange={(v) => up({ name: v })} className="w-56 border-transparent bg-transparent text-sm font-bold hover:border-slate-300" />
        <select
          value={row.status}
          onChange={(e) => up({ status: e.target.value as ContractRow["status"] })}
          className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
        >
          <option value="active">진행 중</option>
          <option value="done">종료</option>
          <option value="cancelled">취소</option>
        </select>
        {dateIn(row.start_date, (v) => up({ start_date: v }), "계약일")}
        <span className="text-xs text-slate-400">~</span>
        {dateIn(row.end_date, (v) => up({ end_date: v }), "쿼터 종료일")}
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-500">
          목표(쿼터)
          <input
            type="number"
            placeholder={String(pr.target ?? "—")}
            value={row.target_qty ?? ""}
            onChange={(e) => up({ target_qty: e.target.value === "" ? null : Number(e.target.value) })}
            className="w-20 rounded border border-slate-300 px-1 py-0.5 text-right text-xs tabular-nums"
          />
          대
        </span>
        {saveMsg && <span className="text-[10px] text-slate-400">{saveMsg}</span>}
        <button
          onClick={() => {
            if (window.confirm(`'${row.name}' 계약을 삭제할까요? (연결 딜은 유지, 실적 행은 함께 삭제됨)`))
              contractsRepo.remove(row.id).then(() => onDeleted?.()).catch((e) => window.alert(String(e)));
          }}
          className="text-xs text-slate-400 hover:text-red-600"
        >
          ✕
        </button>
      </div>

      {/* 거래처·제품 연결 — 딜 프리필의 원천 */}
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="font-semibold text-slate-500">국가</span>
        <TextInput value={row.destination_country} onChange={(v) => up({ destination_country: v.toUpperCase() })} className="w-14" placeholder="IN" />
        <TextInput value={row.country_name ?? ""} onChange={(v) => up({ country_name: v || null })} className="w-20" placeholder="인도" />
        <span className="font-semibold text-slate-500">바이어</span>
        <select
          value={row.buyer_id ?? ""}
          onChange={(e) => {
            const p = partners.find((x) => x.id === e.target.value);
            up({ buyer_id: e.target.value || null, buyer_name: p?.company_name ?? row.buyer_name });
          }}
          className="rounded border border-slate-300 bg-white px-1 py-1 text-xs"
        >
          <option value="">— 거래처 선택 —</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.company_name} ({p.country})
            </option>
          ))}
        </select>
        <TextInput value={row.buyer_name ?? ""} onChange={(v) => up({ buyer_name: v || null })} className="w-32" placeholder="바이어 표기명" />
        <span className="font-semibold text-slate-500">제품</span>
        <select
          value={row.product_id ?? ""}
          onChange={(e) => up({ product_id: e.target.value || null })}
          className="rounded border border-slate-300 bg-white px-1 py-1 text-xs"
        >
          <option value="">— 제품 마스터 —</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.model_name}
            </option>
          ))}
        </select>
      </div>

      {/* 계약 후 단계 설정 — 데모 선반입 / 인허가 / MOQ 쿼터 기산일 */}
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: "#2563eb" }} />
          데모 선반입 {dateIn(row.demo_start, (v) => up({ demo_start: v }))} ~ {dateIn(row.demo_end, (v) => up({ demo_end: v }))}
          <NumInput value={row.demo_qty} onChange={(v) => up({ demo_qty: Number.isFinite(v) ? v : 0 })} className="w-12" />대
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: "#d97706" }} />
          <TextInput value={row.license_label} onChange={(v) => up({ license_label: v })} className="w-28" placeholder="인허가명" />
          {dateIn(row.license_start, (v) => up({ license_start: v }))} ~ {dateIn(row.license_end, (v) => up({ license_end: v }))}
          <label className="flex items-center gap-0.5">
            <input type="checkbox" checked={row.license_done} onChange={(e) => up({ license_done: e.target.checked })} />
            완료
          </label>
        </span>
        <span className="flex items-center gap-1 font-semibold text-emerald-700">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: "#059669" }} />
          MOQ 쿼터 기산일 {dateIn(row.quota_start, (v) => up({ quota_start: v }), "계약 시점과 무관하게 설정 — 통상 인허가 완료 후")}
        </span>
      </div>

      <ContractTimeline ct={ct} today={today} />
      <QuotaTrack ct={ct} sold={pr.sold} />

      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        <span>
          달성률{" "}
          <b className={pr.ratio != null && pr.ratio >= 1 ? "text-emerald-600" : "text-slate-900"}>
            {pr.ratio != null ? pct(pr.ratio) : "— (목표 미설정)"}
          </b>
          {pr.target != null && (
            <span className="text-slate-400">
              {" "}
              ({usd(pr.sold)}/{usd(pr.target)}대{pr.remainToTarget ? ` · 잔여 ${usd(pr.remainToTarget)}대` : " · 달성"})
            </span>
          )}
        </span>
        <span>
          누적 매출 <b>{krw(pr.revenueKrw)}원</b> <span className="text-slate-400">({pr.rowCount}건)</span>
        </span>
        {pr.currentPrice != null && (
          <span>
            현재 적용 구간 단가 <b>${usd(pr.currentPrice)}</b>
          </span>
        )}
        {pr.nextTierAt != null && <span className="text-slate-400">다음 구간 진입까지 {usd(pr.nextTierAt - pr.sold)}대</span>}
        {(pr.freeEarned > 0 || ct.free?.freePerN) && (
          <span>
            무상 발생 <b className="text-blue-700">{usd(pr.freeEarned)}대</b>
            {ct.free?.freePerN ? (
              <span className="text-slate-400">
                {" "}
                (누적 {ct.free.freePerN}대당 {ct.free.freePerUnits}대{pr.nextFreeIn != null ? ` · 다음 무상까지 ${usd(pr.nextFreeIn)}대` : ""})
              </span>
            ) : null}
          </span>
        )}
      </div>

      {/* 달성 전제 페이스 진단 + 전망 곡선 */}
      {(() => {
        const pace = contractPace(ct, pr.sold, pr.target, today);
        if (!pace || pr.target == null) return null;
        const ahead = pace.paceDiff >= 0;
        return (
          <>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
              {!pace.quotaStarted ? (
                <span className="font-semibold text-amber-700">쿼터 기산 전 — {ct.quotaStart || ct.startDate}부터 목표 페이스 계산</span>
              ) : (
                <>
                  <span>
                    기간 경과 <b>{pct(pace.elapsedPct)}</b>
                    <span className="text-slate-400"> (잔여 {usd(pace.remainDays)}일)</span>
                  </span>
                  <span>
                    달성 전제 오늘 목표 <b>{usd(Math.round(pace.expectedQty))}대</b> vs 실적 <b>{usd(pr.sold)}대</b>{" "}
                    <b className={ahead ? "text-emerald-600" : "text-red-600"}>
                      ({ahead ? "+" : ""}
                      {usd(Math.round(pace.paceDiff))}대 {ahead ? "앞섬" : "뒤처짐"})
                    </b>
                  </span>
                  {pace.projectedEnd != null && (
                    <span>
                      현재 페이스 유지 시 최종{" "}
                      <b className={pace.projectedEnd >= pr.target ? "text-emerald-600" : "text-amber-700"}>
                        {usd(Math.round(pace.projectedEnd))}대
                      </b>
                      <span className="text-slate-400"> / 목표 {usd(pr.target)}대</span>
                    </span>
                  )}
                  {pace.requiredMonthly != null && pace.requiredMonthly > 0 && (
                    <span>
                      달성하려면 <b>월 {usd(Math.ceil(pace.requiredMonthly))}대</b> 필요
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="mt-2 rounded border border-slate-200 bg-white p-2">
              <p className="mb-1 text-[11px] font-semibold text-slate-500">달성 전제 목표 누적 vs 실적 vs 현재 페이스 전망</p>
              <PaceChart ct={ct} sales={sales} target={pr.target} today={today} />
            </div>
          </>
        );
      })()}

      {/* 무상 제공 조건 (구간과 독립) */}
      <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-slate-600">
        <span className="font-semibold text-slate-500">무상 조건:</span>
        <span>고정</span>
        <NumInput
          value={row.free?.freeFixed ?? 0}
          onChange={(v) => up({ free: { ...(row.free ?? emptyFree()), freeFixed: Number.isFinite(v) ? v : 0 } })}
          className="w-14"
        />
        <span>대 + 누적</span>
        <input
          type="number"
          placeholder="없음"
          value={row.free?.freePerN ?? ""}
          onChange={(e) => up({ free: { ...(row.free ?? emptyFree()), freePerN: e.target.value === "" ? null : Number(e.target.value) } })}
          className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
        />
        <span>대당</span>
        <NumInput
          value={row.free?.freePerUnits ?? 1}
          onChange={(v) => up({ free: { ...(row.free ?? emptyFree()), freePerUnits: Number.isFinite(v) ? v : 0 } })}
          className="w-14"
        />
        <span>대 무상</span>
        <span className="ml-4 font-semibold text-slate-500">메모</span>
        <TextInput value={row.note} onChange={(v) => up({ note: v })} className="w-64" placeholder="계약 메모" />
      </div>

      {/* 계약 구간표 편집 */}
      <details className="mt-2">
        <summary className="cursor-pointer text-[11px] text-slate-400 hover:text-slate-600">계약 구간표 편집 ({row.tiers.length}구간)</summary>
        <table className="mt-1 w-auto border-collapse">
          <tbody>
            {row.tiers.map((t, ti) => (
              <tr key={ti}>
                <td className="border border-slate-200 p-0.5">
                  <NumInput
                    value={t.fromQty}
                    onChange={(v) => up({ tiers: row.tiers.map((x, j) => (j === ti ? { ...x, fromQty: v } : x)) })}
                    className="w-16"
                  />
                </td>
                <td className="border border-slate-200 p-0.5">
                  <input
                    type="number"
                    placeholder="이상"
                    value={t.toQty ?? ""}
                    onChange={(e) =>
                      up({ tiers: row.tiers.map((x, j) => (j === ti ? { ...x, toQty: e.target.value === "" ? null : Number(e.target.value) } : x)) })
                    }
                    className="w-16 rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums"
                  />
                </td>
                <td className="border border-slate-200 p-0.5">
                  <NumInput
                    value={t.price}
                    step={500}
                    onChange={(v) => up({ tiers: row.tiers.map((x, j) => (j === ti ? { ...x, price: v } : x)) })}
                    className="w-24"
                  />
                </td>
                <td className="w-6 border border-slate-200 text-center">
                  <button
                    onClick={() => row.tiers.length > 1 && up({ tiers: row.tiers.filter((_, j) => j !== ti) })}
                    className="text-xs text-slate-400 hover:text-red-600"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => {
            const last = row.tiers[row.tiers.length - 1];
            const from = last?.toQty != null ? last.toQty + 1 : (last?.fromQty ?? 0) + 10;
            const nt: ContractTier = { fromQty: from, toQty: null, price: 0, freeUnits: 0 };
            up({ tiers: [...row.tiers, nt] });
          }}
          className={btn + " mt-1"}
        >
          + 구간 추가
        </button>
      </details>

      {/* 계약 시점 시뮬레이션 스냅샷 (실현 마진 대비 기준) */}
      {row.sim_snapshot && (
        <p className="mt-2 text-[11px] text-slate-400">
          등록 시점 조건 스냅샷: {row.sim_snapshot.offer_label} · 예상 {usd(row.sim_snapshot.order_qty)}대 · 수수료율{" "}
          {pct(row.sim_snapshot.rate)} · 플로어 ${usd(row.sim_snapshot.floor_company)} · 환율 {usd(row.sim_snapshot.fx)} · 원가{" "}
          {krw(row.sim_snapshot.cost_krw)}원/대
        </p>
      )}
    </div>
  );
}
